import { useEffect, useState } from "react";
import apiClient from "../../services/apiClient";
import LoadingScreen from "../../components/LoadingScreen";
import DynamicTable from "../../components/Table/DataTable";
import TablePaginationActions from "../../components/Table/tablePagination";
import { IconEdit, IconTrash, IconPlus, IconSearch, IconRefresh } from "../../components/icons/icons";
import ChargeCodeDialog from "./PetServiceChargeCodeFormPage";
import CustomSnackbar from "../../components/CustomSnackbar";
import { ChargeCodeStatusModal } from "./ChargeCodeStatusModal";


const ChargeCodesPage = () => {
    const [chargeCodes, setChargeCodes] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [serviceProviders, setServiceProviders] = useState<any[]>([]);
    const [selectedProvider, setSelectedProvider] = useState<number | "">("");
    const [services, setServices] = useState<any[]>([]);
    const [selectedService, setSelectedService] = useState<number | "">("");

    // Dialog state
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isAddMode, setIsAddMode] = useState(false);
    const [formData, setFormData] = useState<any>({});
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "error" | "warning" | "info" | "success";
    }>({ open: false, message: "", severity: "info" });
    const [selectedStatus, setSelectedStatus] = useState<number | ''>('');
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [currentChargeCode, setCurrentChargeCode] = useState<any | null>(null);
    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const fetchChargeCodes = async (
        pageNumber = 0,
        term = "",
        providerId: number | "" = "",
        serviceId: number | "" = "",
        status: number | "" = ""
    ) => {
        try {
            setLoading(true);
            setError("");

            const params: any = {
                limit: rowsPerPage,
                page: pageNumber,
                order_by: "id",
                dir: 2,
            };
            if (term) params.search = term;
            if (providerId) params.service_provider_id = providerId;
            if (serviceId) params.service_id = serviceId;
            if (status !== "") params.is_active = status;

            const queryString = new URLSearchParams(params as any).toString();

            const response: any = await apiClient(
                `v1/resource/ServiceChargeCode?${queryString}`,
                { method: "GET" }
            );

            setChargeCodes(response.data.service_charge_codes || []);
            setTotalCount(response.data.pagination?.total || 0);
        } catch (err: any) {
            setError(err.message || "Failed to fetch charge codes");
        } finally {
            setLoading(false);
        }
    };



    useEffect(() => {
        fetchChargeCodes(page, searchTerm);
    }, [page, rowsPerPage, searchTerm]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
        setPage(0);
    };
    useEffect(() => {
        const fetchServicesByProvider = async () => {
            if (!selectedProvider) {
                setServices([]);
                return;
            }

            try {
                setLoading(true);
                const response: any = await apiClient(
                    `v1/resource/service/all?service_provider_id=${selectedProvider}`,
                    { method: "GET" }
                );
                setServices(response.data.services || []);
            } catch (err: any) {
                setError(err.message || "Failed to fetch services");
            } finally {
                setLoading(false);
            }
        };

        fetchServicesByProvider();
    }, [selectedProvider]);


    const handleSearchClick = () => {
        setSearchTerm(searchInput);
        setPage(0);
        fetchChargeCodes(0, searchInput, selectedProvider, selectedService, selectedStatus);
    };

    const handleOpenEdit = (row: any) => {
        setFormData({
            ...row,
            provider_id: row.service_provider_id,
            name: row.service_name,
            duration: String(row.duration || "0"),
            final_amount: Number(row.total_amount || row.final_amount || 0),
            tax_code: row.additional_info || [],
            service: row.service || {},
            service_id: row.service_id || null,
        });
        setIsAddMode(false);
        setIsDialogOpen(true);
    };




    const handleOpenAdd = () => {
        setFormData({ charge_code: "", service_name: "", amount: 0, duration: 0 });
        setIsAddMode(true);
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            console.log("FormData:", formData);
            setSaving(true);

            // Build payload
            const payload: any = {
                service_provider_id: formData.provider_id,
                charge_code: formData.charge_code,
                service_id: formData.service?.id,
                amount: formData.amount,
                service_name: formData.name,
                total_amount: formData.final_amount,
                service_type: formData.service?.services,
                duration: String(formData.duration),
                tax_codes: formData.tax_code?.tax_codes?.map((t: any) => t.id) || [],
                additional_info: formData.tax_code || [],
            };

            let response: any;

            if (isAddMode) {
                // POST for new charge code
                response = await apiClient("/v1/resource/ServiceChargeCode", {
                    method: "POST",
                    body: payload,
                });
            } else {
                // PATCH for updating existing charge code
                response = await apiClient(`/v1/resource/ServiceChargeCode/${formData.id}`, {
                    method: "PATCH",
                    body: payload,
                });
            }

            setSnackbar({
                open: true,
                message: isAddMode
                    ? "Charge code added successfully!"
                    : "Charge code updated successfully!",
                severity: "success",
            });

            console.log("Payload sent:", JSON.stringify(payload, null, 2));
            setIsDialogOpen(false);

            // Refresh charge codes list
            fetchChargeCodes(page);
        } catch (err: any) {
            console.error(err.message || "Failed to save charge code");
            setSnackbar({
                open: true,
                message: err.message || "Failed to save charge code",
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };


    useEffect(() => {
        const fetchServiceProviders = async () => {
            try {
                setLoading(true);
                const response: any = await apiClient("v1/resource/serviceProvider/all", { method: "GET" });
                setServiceProviders(response.data.service_providers || []);
            } catch (err: any) {
                setError(err.message || "Failed to fetch service providers");
            } finally {
                setLoading(false);
            }
        };
        fetchServiceProviders();
    }, []);
    const handleChange = (key: string, value: any) => {
        setFormData({ ...formData, [key]: value });
    };

    if (loading) return <LoadingScreen message="Loading Charge Codes..." layout={false} />;

    if (error)
        return (
            <div className="text-center mt-10 text-red-500 font-semibold">{error}</div>
        );

    const columns = [
        {
            key: "serial",
            label: "S.No",
            align: "center",
            render: (_: any, index: number) => page * rowsPerPage + index + 1,
        },
        {
            key: "charge_code",
            label: "Charge Code",
            align: "center",
            render: (row: any) => (
                <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
                    {row.charge_code}
                </span>
            ),
        },
        {
            key: "service_name",
            label: "Service Name",
            align: "left",
            render: (row: any) => row.service_name ?? "-",
        },
        {
            key: "duration",
            label: "Duration (mins)",
            align: "center",
            render: (row: any) => {
                console.log("Row duration:", row.duration);
                if (!row.duration) return "-";
                const minutes = Number(row.duration) * 60;
                return `${minutes} mins`;
            },
        },
        {
            key: "amount",
            label: "Base Amount",
            align: "center",
            render: (row: any) => `₹${row.amount ?? 0}`,
        },
        {
            key: "total_amount",
            label: "Total Amount",
            align: "center",
            render: (row: any) => (
                <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded font-semibold">
                    ₹{row.total_amount}
                </span>
            ),
        },
        {
            key: "status",
            label: "Status",
            align: "left",
            render: (row: any) => {
                let bgColor = "";
                let text = "";

                switch (row?.is_active) {
                    case 1:
                        text = "Active";
                        bgColor = "bg-green-100 text-green-800"; // Tailwind example
                        break;
                    case 0:
                        text = "Inactive";
                        bgColor = "bg-yellow-100 text-yellow-800";
                        break;
                    case 2:
                        text = "Deactivated";
                        bgColor = "bg-red-100 text-red-800";
                        break;
                    default:
                        text = "-";
                        bgColor = "bg-gray-100 text-gray-800";
                }

                return (
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${bgColor}`}>
                        {text}
                    </span>
                );
            },
        },
    ]; const openStatusModal = (row: any) => {
        setCurrentChargeCode(row);
        setStatusModalOpen(true);
    };


    const actions = [
        {
            label: "Edit",
            color: "#4caf50",
            icon: <IconEdit size={20} />,
            onClick: handleOpenEdit,
        },
        {
            label: 'Change Status',
            color: '#ff9800',
            icon: <IconRefresh size={20} />,
            onClick: openStatusModal,
        },
        {
            label: "Delete",
            color: "#f44336",
            icon: <IconTrash size={20} />,
            onClick: async (row: any) => {
                const confirmDelete = window.confirm(`Delete charge code ${row.charge_code}?`);
                if (!confirmDelete) return;

                try {
                    // Optional: show loading state
                    setLoading(true);

                    await apiClient(`/v1/resource/ServiceChargeCode/${row.id}`, {
                        method: "DELETE",
                    });

                    // Optional: show snackbar
                    setSnackbar({
                        open: true,
                        message: `Charge code ${row.charge_code} deleted successfully!`,
                        severity: "success",
                    });

                    // Refresh list after deletion
                    fetchChargeCodes(page);
                } catch (err: any) {
                    console.error(err);
                    setSnackbar({
                        open: true,
                        message: err.message || "Failed to delete charge code",
                        severity: "error",
                    });
                } finally {
                    setLoading(false);
                }
            },
        },

    ];
    const handleClearFilters = () => {
        setSelectedProvider("");
        setSelectedService("");
        setSelectedStatus("");
        setSearchInput("");
        setSearchTerm("");
        setPage(0);

        fetchChargeCodes(0, "", "", "", "");
    };

    return (
        <div className="">
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-xl shadow-md">
                <div className="flex flex-1 flex-wrap gap-3 md:gap-4 items-center">
                    {/* Service Provider Filter */}
                    <select
                        value={selectedProvider}
                        onChange={(e) => setSelectedProvider(e.target.value ? Number(e.target.value) : "")}
                        className="border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
                    >
                        <option value="">All Providers</option>
                        {serviceProviders.map((sp) => (
                            <option key={sp.id} value={sp.id}>
                                {sp.user.first_name} {sp.user.last_name}
                            </option>
                        ))}
                    </select>

                    {/* Service Filter */}
                    <select
                        value={selectedService}
                        onChange={(e) => setSelectedService(e.target.value ? Number(e.target.value) : "")}
                        className="border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
                    >
                        <option value="">All Services</option>
                        {services.map((s) => (
                            <option key={s.id} value={s.id}>
                                {s.services_name}
                            </option>
                        ))}
                    </select>

                    {/* Search Input */}
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search charge codes..."
                        className="flex-1 border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition min-w-[200px]"
                    />
                    <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value !== "" ? Number(e.target.value) : "")}
                        className="border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
                    >
                        <option value="">All Status</option>
                        <option value={0}>In Active</option>
                        <option value={1}>Active</option>
                        <option value={2}>Deactivated</option>
                    </select>
                    {/* Search Button */}
                    <button
                        onClick={handleSearchClick}
                        className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700 transition"
                    >
                        <IconSearch size={18} /> Search
                    </button>
                    <button
                        onClick={handleClearFilters}
                        className="flex items-center gap-2 px-5 py-2 bg-gray-300 text-gray-800 rounded-lg shadow hover:bg-gray-400 transition"
                    >
                        Clear Filters
                    </button>
                </div>

                {/* Add Charge Code Button */}
                <button
                    onClick={handleOpenAdd}
                    className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition mt-3 md:mt-0"
                >
                    <IconPlus size={18} /> Add Charge Code
                </button>
            </div>




            <DynamicTable
                columns={columns}
                data={chargeCodes}
                loading={loading}
                emptyMessage={"No charge codes found"}
                page={page}
                actions={actions}
                error={error}
                rowsPerPage={rowsPerPage}
                totalCount={totalCount}
                onPageChange={setPage}
                onRowsPerPageChange={(n: number) => {
                    setRowsPerPage(n);
                    setPage(0);
                }}
                ActionsComponent={TablePaginationActions}
            />


            <ChargeCodeDialog
                open={isDialogOpen}
                isAddMode={isAddMode}
                saving={saving}
                formData={formData}
                onClose={() => setIsDialogOpen(false)}
                onSave={handleSave}
                onChange={handleChange}
            />
            <ChargeCodeStatusModal
                open={statusModalOpen}
                currentChargeCode={currentChargeCode}
                type="service"
                onClose={() => setStatusModalOpen(false)}
                onUpdate={() => fetchChargeCodes(page, searchTerm, selectedProvider, selectedService, selectedStatus)}
            />
            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleSnackbarClose}
            />
        </div>
    );
};

export default ChargeCodesPage;
